import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import UserContext from './context/UserContext';
import TourContext from './context/TourContext';
import { ChatProvider } from './context/ChatContext';
import { PostProvider, usePostContext } from './context/PostContext';
import { getUserPosts } from './utils/actions/PostActions';
import { BrowserRouter as Router, Route, Routes, useParams, Navigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import WelcomePage from './components/WelcomePage';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import ContactUs from './components/ContactUs';
import Help from './components/Help';
import RefundAndCancellation from './components/RefundAndCancellation';
import SignupPage from './components/SignupPage';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import AccountDetails from './components/AccountDetails'
import PaymentInfo from './components/PaymentInfo';
import { UserProvider } from './context/UserContext'; 
import Users from './components/AdminDashboard/Users'
import UserDetails from './components/AdminDashboard/UserDetails';
import {getUserData} from './utils/actions/authActions'
import ForgotPassword from './components/ForgotPassword';
import ProfileRouter from './components/ProfileRouter';
import PublicProfile from './components/PublicProfile'
import EditProfile from './components/ManagerDashboard/EditProfile'
import PostPage from './components/common/PostPage';
import Schedule from './components/ManagerDashboard/Schedule';
import ScheduleCardDetails from './components/ManagerDashboard/ScheduleCardDetails';
import { ThemeProvider } from './context/ThemeContext';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { firebaseInstances } from './utils/firebaseHelper';
import { TourProvider } from './context/TourContext';
import Event from './components/ManagerDashboard/Event';
import Search from './components/ManagerDashboard/Search';
import Crew from './components/ManagerDashboard/Crew';
import Tickets from './components/ManagerDashboard/Tickets';
import CreateTour from './components/ManagerDashboard/CreateTour';
import Membership from './components/ManagerDashboard/Membership';
import Downloads from './components/ManagerDashboard/Downloads';
import Expense from './components/ManagerDashboard/Expense';
import ProfileSettings from './components/ManagerDashboard/ProfileSettings';
import ProfileVerification from './components/ManagerDashboard/ProfileVerification';
import Attachments from './components/ManagerDashboard/Attachments';
import Tasks from './components/ManagerDashboard/Tasks';
import MessagesLayout from './components/ManagerDashboard/MessagesLayout';
import MyBands from './components/common/MyBands'
import BandProfile from './components/common/BandProfile'
import BrandsImporter from './components/BrandsImporter';
import TopBar from './components/TopBar';
import { MembershipProvider } from './context/MembershipContext';
import RoomDetails from './components/ManagerDashboard/RoomDetails';
import TechRider from './components/ManagerDashboard/TechRider';
import GuestList from './components/ManagerDashboard/GuestList';
import Setlist from './components/ManagerDashboard/Setlist';
import EventDateWrapper from './components/EventDateWrapper'; // Renamed from TourDateWrapper
import Inventory from './components/ManagerDashboard/inventory';
import EquipmentDetails from './components/common/EquipmentCardDetails';
import AssignedEquipmentCardDetails from './components/common/AssignedEquipmentCardDetails';
import QRScanHandler from './components/common/QRScanHandler';
import Staff from './components/common/Staff';
import AssignedInventoryTab from './components/ManagerDashboard/AssignedInventoryTab';
import PublicEquipmentCardDetails from './components/ManagerDashboard/PublicEquipmentCardDetails';
import TourChecklist from './components/ManagerDashboard/TourChecklist';
import TourChecklistQRHandler from './components/common/TourChecklistQRHandler';
import VenueProfile from './components/common/VenueProfile';
import RoutesMap from './components/ManagerDashboard/RoutesMap';
import ManagingActs from './components/ProfileSections/ManagingActs';
import OrganizationManagers from './components/ProfileSections/OrganizationManagers';
import VendorStaff from './components/ProfileSections/VendorStaff';
import AssociatedVendors from './components/ProfileSections/AssociatedVendors';
import EPKPage from './components/EPKSection/EPKPage'; 

// Import new mobile components
import BottomNavBar from './components/mobile/BottomNavBar';
import DatesScreen from './components/mobile/DatesScreen';
import RouteScreen from './components/mobile/RouteScreen';

const { firestore: db } = firebaseInstances;

// App Layout with BottomNavBar
const AppLayout = ({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();
  
  // Check if current path is one where TopBar should be hidden
  const hideTopBar = ['/', '/login', '/signup'].includes(location.pathname);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {!isMobile && !hideTopBar && <TopBar />}
      {children}
      {isMobile && <BottomNavBar />}
    </>
  );
};

// Post Wrapper - decides whether to show the modal in profile or standalone page
const PostWrapper = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [postData, setPostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openPostModal } = usePostContext();
  
  useEffect(() => {
    const fetchPostData = async () => {
      try {
        setLoading(true);
        const db = firebaseInstances.firestore;
        const postRef = doc(db, 'posts', postId);
        const postSnap = await getDoc(postRef);
        
        if (postSnap.exists()) {
          const data = { id: postSnap.id, ...postSnap.data() };
          setPostData(data);
          
          // When a user accesses a post URL directly, redirect to profile
          // and open the modal through context
          if (data.userName) {
            // Redirect to profile page
            navigate(`/${data.userName}`, { replace: true });
            
            // Fetch all user posts for modal context
            const userPosts = await getUserPosts(data.userId, 50);
            
            // Open the modal via context once we have the posts
            const postIndex = userPosts.findIndex(p => p.postId === postId);
            if (postIndex !== -1) {
              openPostModal(userPosts, postIndex, `/${data.userName}`);
            }
          }
        } else {
          setError("Post not found");
        }
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("Error loading post");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPostData();
  }, [postId, navigate, openPostModal]);
  
  if (loading) {
    return <div className="loading-spinner"></div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  // For direct access, show the standalone post page
  // But in practice, the redirect will happen before this renders
  return <PostPage postId={postId} postData={postData} />;
};

const UserDetailsWrapper = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userDocRef = doc(db, 'users', userId);
        const userDocSnapshot = await getDoc(userDocRef);
        if (userDocSnapshot.exists()) {
          setUser(userDocSnapshot.data());
        } else {
          console.error('User not found');
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    fetchUser();
  }, [userId]);

  return <UserDetails user={user} />;
};

const ProfileWrapper = () => {
  const { userData, isLoading } = useContext(UserContext);
  const { username } = useParams();
  const location = useLocation();
  
  // Show loading spinner while checking authentication state
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  // Pass location state to ProfileRouter so it can show modal if needed
  return userData ? 
    <ProfileRouter username={username} modalState={location.state} /> : 
    <PublicProfile username={username} />;
};

const RouteGuard = ({ children, allowedUserTypes }) => {
  const { userData } = useContext(UserContext);
  const location = useLocation();
  
  // Check if user is allowed to access this route
  const isAllowed = allowedUserTypes.includes(userData?.userType);
  
  if (!isAllowed) {
    // Redirect to event tab if user is trying to access unauthorized routes
    if (location.pathname.includes('/tour/') || location.pathname.includes('/show/')) {
      const parts = location.pathname.split('/');
      const eventType = parts[1]; // 'tour' or 'show'
      const eventId = parts[2];
      const dateId = parts[3];
      
      if (eventId && dateId) {
        return <Navigate to={`/${eventType}/${eventId}/${dateId}`} state={{ activeTab: 'event' }} />;
      }
    }
    
    // Fallback to profile page
    return <Navigate to={`/${userData?.userName}`} />;
  }
  
  return children;
};

function App() {
  const [userData, setUserData] = useState(null);
  const [authToken, setAuthToken] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        const token = user.getIdToken();
        getUserData(user.uid)
          .then((userData) => {
            setUserData(userData);
            setAuthToken(token);
          })
          .catch((error) => {
            console.error('Error fetching user data:', error);
            // Handle the error scenario
          });
      } else {
        // User is signed out
        const parsedUrl = queryString.parseUrl(window.location.href);
        const { email, token } = parsedUrl.query;
  
        if (token) {
          // Auto-login with token
          signInWithToken(email, token)
            .then((userData) => {
              setUserData(userData);
              setAuthToken(token);
            })
            .catch((error) => {
              console.error('Error during auto-login:', error);
              // Handle the error scenario
            });
        } else {
          setUserData(null);
          setAuthToken(null);
        }
      }
    });
  
    return () => unsubscribe();
  }, []);
  
  const signInWithToken = async (email, token) => {
    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, token);
      const user = userCredential.user;
      const userData = await getUserData(user.uid);
      return userData;
    } catch (error) {
      console.error('Error during sign-in with token:', error);
      throw error;
    }
  };

  // Use a function to get the current location to pass to Routes
  // This ensures the modal behavior works properly with location state
  function RoutesWithLocation() {
    const location = useLocation();
    const background = location.state?.modal ? location.state.previousPath || '/' : null;
    
    return (
      <Routes location={background || location}>
        <Route path="/" element={<WelcomePage />} />
        <Route path="/Terms-and-Conditions" element={<TermsAndConditions />} />
        <Route path="/Contact-Us" element={<ContactUs />} />
        <Route path="/Privacy-Policy" element={<PrivacyPolicy />} />
        <Route path="/help" element={<Help />} />
        <Route path="/Refund-and-Cancellation" element={<RefundAndCancellation />} />
        <Route path="/signup" element={<SignupPage />} />
        
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-bands" element={<MyBands />} />
        <Route path="/routes-map" element={<RoutesMap />} />
        <Route path="/Account-Details" element={<AccountDetails />} />
        <Route path="/Payment-Info" element={<PaymentInfo />} />
        <Route path="/users" element={<Users />} />
        <Route path="/users/:userId" element={<UserDetailsWrapper />} />
        <Route path="/:username/epk" element={<EPKPage />} />
<Route path="/:username/epk/:epkId" element={<EPKPage />} />
        
        {/* Post routes - PostWrapper handles modal vs direct navigation */}
        <Route path="/p/:postId" element={<PostWrapper />} />
        <Route path="/post/:postId" element={<PostWrapper />} />
        
        <Route path="/equipment/:userId/:category/:itemId" element={<PublicEquipmentCardDetails />} />
        
        {/* Redirect to prevent login loop */}
        <Route path="/login" element={
          // Redirect back to the previous page if coming from a post URL
          window.location.pathname.includes('/post/') || window.location.pathname.includes('/p/') 
            ? <Navigate to={window.location.pathname} replace /> 
            : <LoginPage />
        } />
        
        {/* EVENT ROUTES - Support both tour and show types */}
        {/* Event main view */}
        <Route path="/event/:eventId/:dateId" element={
          <EventDateWrapper>
            <Event />
          </EventDateWrapper>
        } />
        
        {/* Tour-specific routes */}
        <Route path="/tour/:eventId/:dateId" element={
          <EventDateWrapper eventType="tour">
            <Schedule />
          </EventDateWrapper>
        } />
        <Route path="/tour/:eventId/:dateId/schedule/:scheduleId" element={
          <EventDateWrapper eventType="tour">
            <ScheduleCardDetails />
          </EventDateWrapper>
        } />
        <Route path="/tour/:eventId/:dateId/attachments" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="tour">
              <Attachments />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/tour/:eventId/:dateId/tasks" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="tour">
              <Tasks />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/tour/:eventId/:dateId/techrider" element={
          <EventDateWrapper eventType="tour">
            <TechRider />
          </EventDateWrapper>
        } />
        <Route path="/tour/:eventId/:dateId/checklist" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="tour">
              <TourChecklist />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/tour/:eventId/:dateId/guestlist" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="tour">
              <GuestList />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/tour/:eventId/:dateId/setlist" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="tour">
              <Setlist />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/tour/:eventId/:dateId/hotel/:hotelId" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="tour">
              <RoomDetails />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/tour/:eventId/:dateId/assigned-inventory" element={
          <EventDateWrapper eventType="tour">
            <AssignedInventoryTab />
          </EventDateWrapper>
        } />
        <Route path="/tour/:eventId/:dateId/assigned-inventory/:groupId" element={
          <EventDateWrapper eventType="tour">
            <AssignedEquipmentCardDetails />
          </EventDateWrapper>
        } />
        <Route path="/tour/:eventId/:dateId/route" element={
          <EventDateWrapper eventType="tour">
            <RouteScreen />
          </EventDateWrapper>
        } />
        
        {/* Show-specific routes - mirror of tour routes */}
        <Route path="/show/:eventId/:dateId" element={
          <EventDateWrapper eventType="show">
            <Schedule />
          </EventDateWrapper>
        } />
        <Route path="/show/:eventId/:dateId/schedule/:scheduleId" element={
          <EventDateWrapper eventType="show">
            <ScheduleCardDetails />
          </EventDateWrapper>
        } />
        <Route path="/show/:eventId/:dateId/attachments" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="show">
              <Attachments />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/show/:eventId/:dateId/tasks" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="show">
              <Tasks />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/show/:eventId/:dateId/techrider" element={
          <EventDateWrapper eventType="show">
            <TechRider />
          </EventDateWrapper>
        } />
        <Route path="/show/:eventId/:dateId/checklist" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="show">
              <TourChecklist />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/show/:eventId/:dateId/guestlist" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="show">
              <GuestList />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/show/:eventId/:dateId/setlist" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="show">
              <Setlist />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/show/:eventId/:dateId/hotel/:hotelId" element={
          <RouteGuard allowedUserTypes={['Manager', 'Artist', 'Crew', "Organization"]}>
            <EventDateWrapper eventType="show">
              <RoomDetails />
            </EventDateWrapper>
          </RouteGuard>
        } />
        <Route path="/show/:eventId/:dateId/assigned-inventory" element={
          <EventDateWrapper eventType="show">
            <AssignedInventoryTab />
          </EventDateWrapper>
        } />
        <Route path="/show/:eventId/:dateId/assigned-inventory/:groupId" element={
          <EventDateWrapper eventType="show">
            <AssignedEquipmentCardDetails />
          </EventDateWrapper>
        } />
        <Route path="/show/:eventId/:dateId/route" element={
          <EventDateWrapper eventType="show">
            <RouteScreen />
          </EventDateWrapper>
        } />
        
        {/* Other routes */}
        <Route path="/venue/:placeId" element={<VenueProfile />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/search" element={<Search />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/staff" element={<VendorStaff />} />
        <Route path="/managers" element={<OrganizationManagers />} />
        <Route path="/managing-acts" element={<ManagingActs />} />
        <Route path="/associated-vendors" element={<AssociatedVendors />} />
        <Route path="/inventory/:category/:itemId" element={<EquipmentDetails />} />
        <Route path="/scan/assigned-inventory/:itemId" element={<QRScanHandler />} />
        <Route path="/scan/tour-checklist/:itemId" element={<TourChecklistQRHandler />} />
        <Route path="/Crew" element={<Crew />} />
        <Route path="/Tickets" element={<Tickets />} />
        <Route path="/Membership" element={<Membership />} />
        <Route path="/Downloads" element={<Downloads />} />
        <Route path="/Expense" element={<Expense />} />
        <Route path="/Settings" element={<ProfileSettings />} />
        <Route path="/verification" element={<ProfileVerification />} />
        <Route path="/create-tour" element={<CreateTour />} />
        <Route path="/messages" element={<MessagesLayout />} />
        
        {/* Mobile Routes */}
        <Route path="/dates" element={<DatesScreen />} />
        
        {/* User routes */}
        <Route path="/:username" element={<ProfileWrapper />} />
        <Route path="/edit-profile" element={<EditProfile />} />
        <Route path="/bands/:bandUsername" element={<BandProfile />} />
        <Route path="/Import-Brands" element={<BrandsImporter />} />
      </Routes>
    );
  }

  return (
    <ThemeProvider>
      <UserProvider>
        <TourProvider>
          <ChatProvider>
            <MembershipProvider>
              <PostProvider> 
                <Router>
                  <AppLayout>
                    <RoutesWithLocation />
                  </AppLayout>
                </Router>
              </PostProvider>
            </MembershipProvider>
          </ChatProvider>
        </TourProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;